import type { Session } from "./types";
import { hasString, isJsonObject, strictJsonEnvelope, withRequestTimeout, type JsonObject } from "./json";

interface AuthenticationOptionsEnvelope {
  ok: boolean;
  options: PublicKeyCredentialRequestOptionsJSON;
}

interface PublicKeyCredentialRequestOptionsJSON extends Omit<PublicKeyCredentialRequestOptions, "challenge" | "allowCredentials"> {
  challenge: string;
  allowCredentials?: Array<Omit<PublicKeyCredentialDescriptor, "id"> & { id: string }>;
}

export class AuthError extends Error {
  constructor(public status: number, public code: string) {
    super(code);
  }
}

export function isCanonicalUserId(value: unknown): value is string {
  return typeof value === "string" && /^usr_[a-f0-9]{32}$/.test(value);
}

export function isStructurallyValidSession(value: unknown): value is Session {
  if (!isJsonObject(value)
    || !isCanonicalUserId(value.user_id)
    || !boundedNonBlankString(value.email, 320)
    || !boundedNonBlankString(value.display_name, 160)
    || !hasString(value, "expires_at")
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value.expires_at as string)) return false;
  return Number.isFinite(Date.parse(value.expires_at as string));
}

export function isUsableSession(value: unknown, now = Date.now()): value is Session {
  if (!isStructurallyValidSession(value)) return false;
  const expiresAt = Date.parse(value.expires_at as string);
  return expiresAt > now;
}

function base64urlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  return bytes.buffer;
}

function bufferToBase64url(value: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(value));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function publicKeyRequestOptions(options: PublicKeyCredentialRequestOptionsJSON): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((credential) => ({
      ...credential,
      id: base64urlToBuffer(credential.id),
    })),
  };
}

export function credentialToJSON(credential: PublicKeyCredential): object {
  const modern = credential as PublicKeyCredential & { toJSON?: () => object };
  if (modern.toJSON) return modern.toJSON();
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : undefined,
    },
  };
}

async function authJson<T>(path: string, init: RequestInit | undefined, validate: (payload: JsonObject) => boolean): Promise<T> {
  return withRequestTimeout(init?.signal, async (signal) => {
    const response = await fetch(path, { ...init, credentials: "same-origin", signal });
    const payload = await strictJsonEnvelope(response, (status, code) => new AuthError(status, code), "authentication_failed");
    if (!validate(payload)) throw new AuthError(response.status, "invalid_response");
    return payload as T;
  });
}

function validSessionEnvelope(payload: JsonObject): boolean {
  return isUsableSession(payload.session);
}

function validAuthenticationOptions(payload: JsonObject): boolean {
  const options = payload.options;
  if (!isJsonObject(options)
    || !boundedBase64url(options.challenge, 16, 1024)
    || (options.timeout !== undefined && (!Number.isSafeInteger(options.timeout) || (options.timeout as number) < 1 || (options.timeout as number) > 300_000))
    || (options.rpId !== undefined && !validRpId(options.rpId))
    || (options.userVerification !== undefined && !["required", "preferred", "discouraged"].includes(String(options.userVerification)))) return false;
  if (options.allowCredentials === undefined) return true;
  if (!Array.isArray(options.allowCredentials) || options.allowCredentials.length > 64) return false;
  const ids = new Set<string>();
  return options.allowCredentials.every((credential) => {
    if (!isJsonObject(credential)
      || credential.type !== "public-key"
      || !boundedBase64url(credential.id, 1, 2048)
      || ids.has(credential.id as string)) return false;
    ids.add(credential.id as string);
    return credential.transports === undefined
      || (Array.isArray(credential.transports)
        && credential.transports.length <= 8
        && credential.transports.every((transport) => typeof transport === "string" && AUTHENTICATOR_TRANSPORTS.has(transport)));
  });
}

const AUTHENTICATOR_TRANSPORTS = new Set(["ble", "hybrid", "internal", "nfc", "smart-card", "usb"]);

function boundedNonBlankString(value: unknown, maximum: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= maximum && !/[\u0000-\u001f\u007f]/.test(value);
}

function boundedBase64url(value: unknown, minimum: number, maximum: number): value is string {
  if (!(typeof value === "string"
    && value.length >= minimum
    && value.length <= maximum
    && value.length % 4 !== 1
    && /^[A-Za-z0-9_-]+$/.test(value))) return false;
  try {
    return bufferToBase64url(base64urlToBuffer(value)) === value;
  } catch {
    return false;
  }
}

function validRpId(value: unknown): boolean {
  if (typeof value !== "string" || value.length < 1 || value.length > 253 || value !== value.toLowerCase()) return false;
  return value === "localhost" || /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value);
}

export async function currentSession(): Promise<Session> {
  const payload = await authJson<{ ok: boolean; session: Session }>("/auth/session", undefined, validSessionEnvelope);
  return payload.session;
}

export async function signInWithPasskey(): Promise<Session> {
  if (!window.PublicKeyCredential) throw new Error("Passkeys are not available in this browser.");
  const options = await authJson<AuthenticationOptionsEnvelope>("/auth/login/options", { method: "POST" }, validAuthenticationOptions);
  const credential = await navigator.credentials.get({ publicKey: publicKeyRequestOptions(options.options) });
  if (!(credential instanceof PublicKeyCredential)) throw new Error("Passkey sign-in was cancelled.");
  const payload = await authJson<{ ok: boolean; session: Session }>("/auth/login/verify", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(credentialToJSON(credential)),
  }, validSessionEnvelope);
  return payload.session;
}

export async function signOutSession(): Promise<void> {
  const response = await withRequestTimeout(undefined, (signal) => fetch("/auth/logout", { method: "POST", credentials: "same-origin", signal }));
  if (!response.ok && response.status !== 401) throw new Error("sign_out_failed");
}
